import type { FormElementProps } from '@magicschool/business-logic/forms';
import type { FieldVisibility, InputsType, SafeToolInfo } from '@magicschool/business-logic/tools';
import { logger } from '@magicschool/logger';
import { toast } from '@magicschool/spellbook';
import type { Enums, Json } from '@magicschool/supabase/types';
import { emptyToolCustomization, generateValidToolCustomization, type ToolCustomization } from 'features/rooms/types';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import type { IntlShape } from 'react-intl';
import { v4 as uuid } from 'uuid';
import { cleanupDraftToolCustomization } from '@/app/_actions/tool-customizations/cleanupDraftToolCustomization';
import { createDraftToolCustomization } from '@/app/_actions/tool-customizations/createDraftToolCustomization';
import { createNewToolCustomizationServerAction, updateToolCustomizationServerAction } from '@/app/(teacher)/tools/actions';
import type { FormBuilderValues } from '@/components/FormBuilder/FormBuilder';
import { type DocumentIngestionState, toFileState } from '@/features/knowledge-base/documentIngestionStatus';
import type { DownloadableFile, FileState } from '@/features/rag/types';
import { storage } from '@/features/storage';
import { createStoreSlice, type SetField } from '@/features/store/zustand';
import { isChatbotMigrationAdmitted } from '@/features/tools/migrationStatus';
import { playTtsPreviewAudio } from '@/features/tts/playTtsPreviewAudio';
import { releaseAudioElement } from '@/features/tts/preserveAudioUserGesture';
import type { Voice } from '@/features/tts/types';
import { client } from '@/hooks/useQuery';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import { ImmutableList, ImmutableMap } from '@/util/immutable';
import { toolDocumentsQueryKey } from './useToolDocumentStatuses';
import { removeFiles } from './utils/removeFiles';
import { removeFilesFromCds } from './utils/removeFilesFromCds';
import { uploadFiles } from './utils/uploadFiles';
import { uploadFilesToCds } from './utils/uploadFilesToCds';

export type DefaultToolCustomizationStep = 'edit' | 'customInstructions' | 'test' | 'access';
export type RAGToolCustomizationStep = 'edit' | 'prepare' | 'test' | 'access';

export type CustomizingStep = DefaultToolCustomizationStep | RAGToolCustomizationStep;

export type SetToolCustomizationVisibility = (name: string, visibility: FieldVisibility) => void;

export type ToolCustomizationStore = {
  toolToCustomize: SafeToolInfo | null;
  existingToolCustomization: ToolCustomization | null;
  tempToolCustomization: ToolCustomization | null;
  templateNameModalOpen: boolean;
  templateName: string;
  invalidStateModalOpen: boolean;
  invalidFields: ImmutableList<FormElementProps>;
  toolCustomizationTemplatesMap: ImmutableMap<string, ImmutableList<ToolCustomization>>;
  cancelModalOpen: boolean;
  searchTerm: string;
  selectedCategory: string;
  selectedGradeLevel: string;
  customizingStep: CustomizingStep;
  toolApplication: Enums<'tool_application_type'> | null;
  audioElement: HTMLAudioElement | null;
  isNewTool: boolean;
  isPolling: boolean;
  isLoadingAudio: boolean;
  files: DownloadableFile<FileState>[];
  queuedUploads: File[];
  // EAS-365 (4b): when true, a MIGRATED custom chatbot's document uploads/deletes go through the CDS
  // per-tool route instead of the legacy OpenAI/TUS path. Combined with an admitted migration_status
  // (checked at use). Defaults false — every non-migrated bot keeps the exact legacy behavior.
  cdsWritePathEnabled: boolean;

  startToolCustomization: (args: {
    intl: IntlShape;
    tool: SafeToolInfo;
    toolApplication: Enums<'tool_application_type'>;
    existingCustomization?: ToolCustomization;
    isNewOverride?: boolean;
    files?: (DownloadableFile & { state?: FileState })[];
    editMode?: boolean; // note: if this is false, but there is an existing customization, this mean we are creating a new customization FROM an existing one
    cdsWritePathEnabled?: boolean;
  }) => Promise<void>;
  setToolCustomizationVisibility: SetToolCustomizationVisibility;
  updateCustomizedTool: (values: FormBuilderValues) => void;
  saveCustomToolTitle: (title: string) => void;
  saveCustomToolDescription: (description: string) => void;
  setCustomToolVoice: (voice: Voice) => void;
  testVoice: (intl: IntlShape) => void;
  saveCustomInstructions: (value: InputsType[string]) => void;
  cancelCustomizeTool: () => boolean;
  deleteToolTemplate: (tc: ToolCustomization) => Promise<void>;
  addToolTemplate: () => Promise<void>;
  selectToolTemplate: (selected: ToolCustomization) => void;
  openTemplateNameModal: () => void;
  resetCustomization: () => void;
  discardToolChanges: () => void;
  validateFields: (tool: SafeToolInfo, customization: ToolCustomization) => boolean;
  load: () => void;
  executeSearch: (searchTerm: string) => void;
  setField: SetField<ToolCustomizationStore>;
  setStep: (step: CustomizingStep) => Promise<void>;
  /** Refresh file ingestion states from a freshly-fetched document list (see useToolDocumentStatuses). */
  syncFileIngestionStates: (docs: (DocumentIngestionState & { id: string })[]) => void;
  onFilesAdded: (files: File[], intl: IntlShape) => Promise<void>;
  onFilesRemoved: (files: DownloadableFile[], intl: IntlShape) => Promise<void>;
  failQueuedUploads: (pending: File[], intl: IntlShape) => void;
  isAssistantTool(): boolean;
  pollingCancelFn?: () => void;
  clearCustomizationTool: () => void;
};

const defaultState = {
  searchTerm: '',
  selectedCategory: '',
  toolToCustomize: null,
  existingToolCustomization: null,
  tempToolCustomization: null,
  templateNameModalOpen: false,
  templateName: '',
  toolCustomizationTemplatesMap: ImmutableMap<string, ImmutableList<ToolCustomization>>(),
  invalidStateModalOpen: false,
  invalidFields: ImmutableList<FormElementProps>(),
  cancelModalOpen: false,
  selectedGradeLevel: 'pre-k',
  customizingStep: 'edit' as const,
  toolApplication: null,
  audioElement: null,
  isNewTool: true,
  isLoadingAudio: false,
  files: [],
  isPolling: false,
  queuedUploads: [],
  cdsWritePathEnabled: false,
};

export const createToolCustomizationStoreSlice = createStoreSlice(
  'ToolCustomizationStoreData',
  { ...defaultState },
  ({ set, get, getFull, setField }) => ({
    setField,
    startToolCustomization: async ({
      tool: td,
      toolApplication,
      existingCustomization,
      isNewOverride,
      files,
      editMode,
      cdsWritePathEnabled,
    }) => {
      const { toolToCustomize: currentTool, customizingStep: currentStep, toolApplication: currentToolApplication } = get();
      // Guard against RSC-triggered remounts resetting an in-progress customization.
      // If we're already customizing the exact same tool+application combo and have
      // moved past the initial edit step, skip re-initialization to preserve state.
      // Comparing toolApplication prevents same-slug org vs individual from being skipped.
      // clearCustomizationTool() (sets toolToCustomize=null) is called on all normal exit
      // paths so a fresh session always passes through.
      if (currentTool && currentTool.tool.slug === td.tool.slug && currentToolApplication === toolApplication && currentStep !== 'edit') {
        return;
      }

      const { toolCustomizationTemplatesMap, selectedGradeLevel, ...resetState } = { ...defaultState };
      const { pollingCancelFn } = get();
      set({ ...resetState, isPolling: false });

      if (pollingCancelFn) {
        pollingCancelFn();
        set({ pollingCancelFn: undefined });
      }

      getFull().ChatStoreData.clearChatStore();

      const initialGradeLevel = storage.getItem(`last-grade-level-selected`) || 'pre-k';
      let tempToolCustomization: ToolCustomization;
      try {
        if (existingCustomization && ((!isEmpty(existingCustomization.json_config) && !td.tool.assistant_tool_type) || editMode)) {
          tempToolCustomization = generateValidToolCustomization(td.fields, existingCustomization);
        } else {
          if (td.tool.assistant_tool_type) {
            if (existingCustomization && !isEmpty(existingCustomization.json_config)) {
              // TODO ENG-2171 merge in the json config stuff because we're customizing off of this
              logger.warn('attempt to customize an existing RAG tool customization with a json_config');
            }
            const emptyToolShell = {
              ...emptyToolCustomization(td, initialGradeLevel, toolApplication),
              status: 'draft' as const,
            };
            const result = await createDraftToolCustomization(emptyToolShell);
            if (result.error || !result.data) {
              throw new Error('Error creating tool customization');
            }
            tempToolCustomization = result.data;
          } else {
            tempToolCustomization = emptyToolCustomization(td, initialGradeLevel, toolApplication);
          }
        }

        set({
          selectedGradeLevel: initialGradeLevel,
          toolToCustomize: td,
          tempToolCustomization,
          existingToolCustomization: tempToolCustomization,
          toolApplication,
          isNewTool: isNewOverride || !existingCustomization,
          // Server-supplied files already carry their real ingestion state (see toFileState); only
          // legacy assistant files, which have no ingestion of their own, default to ready.
          files: files?.map((file) => ({ ...file, state: file.state ?? ('idle' as const) })) || [],
          cdsWritePathEnabled: cdsWritePathEnabled ?? false,
        });
      } catch (error) {
        console.error(error);
      }
    },
    saveCustomToolTitle: (title) => {
      const { toolToCustomize, tempToolCustomization } = get();
      if (!toolToCustomize || !tempToolCustomization) return;

      const clone = structuredClone(tempToolCustomization);
      clone.json_config.tool_title = title;
      set({ tempToolCustomization: clone });
    },
    saveCustomToolDescription: (description) => {
      const { toolToCustomize, tempToolCustomization } = get();
      if (!toolToCustomize || !tempToolCustomization) return;

      const clone = structuredClone(tempToolCustomization);
      clone.json_config.tool_description = description;
      set({ tempToolCustomization: clone });
    },
    setCustomToolVoice: (voice) => {
      const { toolToCustomize, tempToolCustomization } = get();
      if (!toolToCustomize || !tempToolCustomization) return;

      const clone = structuredClone(tempToolCustomization);
      clone.json_config.voice = voice;
      set({ tempToolCustomization: clone });
    },
    saveCustomInstructions: (value) => {
      const { toolToCustomize, tempToolCustomization } = get();
      if (!toolToCustomize || !tempToolCustomization) return;

      const clone = structuredClone(tempToolCustomization);
      clone.json_config.customInstructions = value;
      set({ tempToolCustomization: clone });
    },
    testVoice: async (intl) => {
      set({ isLoadingAudio: true });
      const { tempToolCustomization, audioElement } = get();

      if (audioElement) {
        releaseAudioElement(audioElement);
        set({ audioElement: null });
      }

      const voice = (tempToolCustomization?.json_config.voice || 'nova') as Voice;

      try {
        const audio = await playTtsPreviewAudio({ voice });
        set({ audioElement: audio, isLoadingAudio: false });
      } catch {
        set({ isLoadingAudio: false });
        toast({ title: intl.formatMessage({ id: 'voice-test.error' }), status: 'error' });
        throw Error('Failed to fetch audio');
      }
    },
    setToolCustomizationVisibility: (name, value) => {
      const { tempToolCustomization, toolToCustomize, validateFields } = get();
      if (!toolToCustomize || !tempToolCustomization) return;
      const clone = structuredClone(tempToolCustomization);
      clone.json_config.inputs[name].visibility = value;
      validateFields(toolToCustomize, clone);
      set({ tempToolCustomization: clone });
    },
    updateCustomizedTool: (values) => {
      const { tempToolCustomization, toolToCustomize, validateFields } = get();
      if (!toolToCustomize || !tempToolCustomization) return;
      const clone = structuredClone(tempToolCustomization);
      const fieldNames = Object.keys(clone.json_config.inputs);
      for (const name of fieldNames) {
        clone.json_config.inputs[name].value = values[name];
      }

      validateFields(toolToCustomize, clone);
      set({ tempToolCustomization: clone });
    },
    cancelCustomizeTool: (): boolean => {
      const { tempToolCustomization, toolToCustomize, discardToolChanges, existingToolCustomization, files } = get();
      if (!tempToolCustomization || !toolToCustomize) return true;

      const dirty = !isEqual(existingToolCustomization?.json_config, tempToolCustomization.json_config) || files.length > 0;
      if (dirty) {
        set({ cancelModalOpen: true });
        return false; // Indicate that we should not navigate away
        // Do not navigate here - let the modal handle navigation
      } else {
        discardToolChanges();
        return true;
      }
    },
    addToolTemplate: async () => {
      const { tempToolCustomization, templateName, toolToCustomize, validateFields } = get();
      if (!tempToolCustomization || !toolToCustomize) return;

      const fieldsValid = validateFields(toolToCustomize, tempToolCustomization);
      if (!fieldsValid) {
        set({ invalidStateModalOpen: true });
        return;
      }

      const newCustomization = await createNewToolCustomizationServerAction({
        name: templateName,
        tool_slug: tempToolCustomization.tool_slug,
        tool_uuid: tempToolCustomization.tool_uuid,
        json_config: tempToolCustomization.json_config,
        tool_application: 'room',
        source_customization_id: tempToolCustomization.id || null,
      });
      const data = newCustomization.data;

      set((s) => ({
        templateNameModalOpen: false,
        templateName: '',
        toolCustomizationTemplatesMap: data
          ? s.toolCustomizationTemplatesMap.update(tempToolCustomization.tool_uuid, ImmutableList(), (tcs) => tcs.push(data))
          : s.toolCustomizationTemplatesMap,
      }));
    },
    deleteToolTemplate: async (tc) => {
      const response = await fetch(`/api/tool_customizations/${tc.id}`, { method: 'DELETE' });
      if (!response.ok) return;

      set((s) => ({
        toolCustomizationTemplatesMap: s.toolCustomizationTemplatesMap.update(tc.tool_uuid, ImmutableList(), (tcs) =>
          tcs.filter((t) => t.id !== tc.id),
        ),
      }));
    },
    selectToolTemplate: (selected) => {
      const { tempToolCustomization } = get();
      if (!tempToolCustomization) return;
      set({ tempToolCustomization: { ...tempToolCustomization, json_config: { ...selected.json_config } } });
    },
    openTemplateNameModal: () => {
      const { tempToolCustomization, toolToCustomize, validateFields } = get();
      if (!tempToolCustomization || !toolToCustomize) return;

      // We don't want to allow users to save invalid templates
      const fieldsValid = validateFields(toolToCustomize, tempToolCustomization);
      const key = fieldsValid ? 'templateNameModalOpen' : 'invalidStateModalOpen';
      set({ [key]: true });
    },
    discardToolChanges: () => {
      const { pollingCancelFn } = get();
      if (pollingCancelFn) {
        pollingCancelFn();
        set({ pollingCancelFn: undefined });
      }
      const { isNewTool, tempToolCustomization, isAssistantTool } = get();
      const { toolCustomizationTemplatesMap, selectedGradeLevel, ...resetState } = { ...defaultState };

      if (isNewTool && isAssistantTool()) {
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        cleanupDraftToolCustomization(tempToolCustomization?.id!)
          .then(() => logger.debug('Successfully cleaned up draft tool customization'))
          .catch((e) => logger.error('Error cleaning up draft tool customization', e));
      }
      set(resetState);
    },
    resetCustomization: () => {
      const { toolToCustomize, selectedGradeLevel, tempToolCustomization, toolApplication } = get();
      if (!toolToCustomize) return;

      const toolCustomizationReset = {
        ...emptyToolCustomization(toolToCustomize, selectedGradeLevel, toolApplication ?? undefined),
        id: tempToolCustomization?.id || '',
        // emptyToolCustomization defaults to 'active', which would enable shouldDisableRAGFields
        // while the user is still configuring a draft. Keep current status.
        status: tempToolCustomization?.status ?? 'draft',
        // Keep the assistant so post-reset uploads are not queued with no poll running.
        rag_assistant_id: tempToolCustomization?.rag_assistant_id ?? null,
      };
      set({ tempToolCustomization: toolCustomizationReset, invalidStateModalOpen: false, files: [] });
    },
    validateFields: (tool, customization) => {
      const invalidFields = ImmutableList(tool.fields).filter((f) => {
        const overrides = customization.json_config.inputs[f.name];

        // If the field is required and hidden and has no value, it's invalid because the
        // students would not be able to see it or fill it out
        return Boolean(overrides.visibility === 'hidden' && f.required && !overrides.value);
      });

      set({ invalidFields });

      return invalidFields.isEmpty();
    },
    load: () => {
      const { toolCustomizationTemplatesMap, selectedGradeLevel, ...resetState } = { ...defaultState };
      set({ ...resetState });
    },
    executeSearch: (searchTerm: string) => {
      set({ searchTerm, selectedCategory: '' });
    },
    syncFileIngestionStates: (docs) => {
      const byId = new Map(docs.map((doc) => [doc.id, doc]));

      set((state) => ({
        files: state.files.map((entry) => {
          // Only server-backed rows have a document id to match on. A file mid-upload has no id
          // yet and must keep its own optimistic state, or a poll landing during an upload would
          // knock it back to a state the upload flow is still driving.
          const doc = entry.fileId ? byId.get(entry.fileId) : undefined;
          if (!doc || entry.state === 'uploading' || entry.state === 'removing') return entry;

          return { ...entry, state: toFileState(doc) };
        }),
      }));
    },

    onFilesAdded: async (newFiles, intl) => {
      const MAX_FILE_SIZE = 50 * 1024 * 1024;

      const tooLarge = newFiles.filter((file) => file.size > MAX_FILE_SIZE);
      if (tooLarge.length > 0) {
        for (const file of tooLarge) {
          analyticsTrack('GenerationForm:FileUploadError', { type: 'FileTooLarge', size: file.size });
        }

        toast({ title: intl.formatMessage({ id: 'rag.file-upload.file-size-limit-error' }), status: 'error' });
      }

      const incomingFiles = newFiles.filter((file) => file.size <= MAX_FILE_SIZE);

      // Capture existing CDS entries by name BEFORE the optimistic replace below. On the CDS path a
      // same-name re-upload creates a NEW document (no name-deterministic x-upsert like the legacy path),
      // so we keep the prior entry to (a) delete the superseded doc when the new upload SUCCEEDS and
      // (b) restore the prior entry when it FAILS — otherwise the old doc stays live + searchable in the
      // bot's KB with no UI affordance to remove it.
      const priorCdsEntryByName = new Map<string, DownloadableFile<FileState>>();
      for (const entry of get().files) {
        if (entry.fileId) priorCdsEntryByName.set(entry.name, entry);
      }

      set((state) => ({
        files: state.files
          .filter((entry) => !incomingFiles.some((f) => f.name === entry.name))
          .concat(
            incomingFiles.map((file) => ({
              downloadPath: URL.createObjectURL(file),
              name: file.name,
              size: file.size,
              type: file.type,
              state: 'uploading' as const,
            })),
          ),
      }));

      const { tempToolCustomization, isPolling, cdsWritePathEnabled } = get();
      if (!tempToolCustomization) return;

      // EAS-365 (4b): a migrated + admitted bot uploads into its CDS KB via the per-tool route; every
      // other bot keeps the legacy OpenAI/TUS path unchanged.
      const useCds = cdsWritePathEnabled && isChatbotMigrationAdmitted(tempToolCustomization);

      const onUploadError = (file: File) => {
        analyticsTrack('GenerationForm:FileUploadError', { type: 'UploadError' });
        toast({ title: intl.formatMessage({ id: 'rag.file-upload.general-error' }, { file: file.name }), status: 'error' });
      };
      const onUploadSuccess = () => analyticsTrack('GenerationForm:FileUpload');

      try {
        const { results, queued } = useCds
          ? await uploadFilesToCds({
              files: incomingFiles,
              customizationId: tempToolCustomization.id,
              onError: onUploadError,
              onSuccess: onUploadSuccess,
            })
          : await uploadFiles({
              files: incomingFiles,
              ragAssistantId: tempToolCustomization.rag_assistant_id,
              isPolling,
              onError: onUploadError,
              onSuccess: onUploadSuccess,
              onBrowserNotSupported: () => {
                analyticsTrack('GenerationForm:FileUploadError', { type: 'BrowserNotSupported', browser: navigator.userAgent });
              },
            });

        if (queued) {
          // Functional update: `queuedUploads` captured above can be stale if another call mutated it
          // while we awaited uploadFiles.
          set((state) => ({ queuedUploads: [...state.queuedUploads, ...newFiles] }));

          // If the assistant-id poll already settled during the await above, its drain handler may have
          // run before these files were enqueued, which would strand them in 'uploading'. Reconcile here.
          const { isPolling: stillPolling, tempToolCustomization: tc } = get();
          if (!stillPolling) {
            const { queuedUploads: pending } = get();
            set({ queuedUploads: [] });
            if (tc?.rag_assistant_id) {
              // Poll succeeded: upload the queued files for real now that the id exists.
              await get().onFilesAdded(pending, intl);
            } else {
              // Poll failed/timed out: surface an error instead of leaving the spinner running.
              get().failQueuedUploads(pending, intl);
            }
          }
          return;
        }

        const successful: string[] = [];
        const failures: Record<string, string> = {};
        // Keep the id the uploader returned (the real CDS document id on the migrated path) so deletes
        // can target it; the legacy path returns a uuid, which is fine (it deletes by filename).
        const idByName = new Map<string, string>();

        for (const r of results) {
          if (r.status === 'fulfilled') {
            if (r.value.fileId) {
              successful.push(r.value.fileName);
              idByName.set(r.value.fileName, r.value.fileId);
            } else if (r.value.error) {
              failures[r.value.fileName] = r.value.error;
            }
          } else {
            // that means it was 'rejected'
            failures[r.reason.fileName] = r.reason.error;
          }
        }

        set((state) => ({
          files: state.files.map((entry) => {
            if (!newFiles.some((f) => f.name === entry.name)) {
              return entry;
            }
            if (successful.includes(entry.name)) {
              // A finished upload is stored, not searchable: the CDS path has only just queued
              // ingestion, which takes minutes. Landing on 'idle' here is what made a document look
              // ready the moment its bytes arrived, and — because the status poll only runs while
              // something is 'processing' — also stopped anything from ever correcting it. The
              // legacy path has no ingestion of its own, so for it the upload genuinely is the end.
              return {
                ...entry,
                fileId: idByName.get(entry.name) ?? uuid(),
                state: useCds ? ('processing' as const) : ('idle' as const),
              };
            }
            if (failures[entry.name]) {
              // CDS same-name replace that FAILED: no new document was created, so the prior document is
              // still live server-side. Restore the prior entry (idle + its fileId) so it stays visible +
              // removable instead of leaving a fileId-less error row while the old doc lingers unremovable
              // in retrieval. The failure was already surfaced via onUploadError's toast.
              const priorCds = useCds ? priorCdsEntryByName.get(entry.name) : undefined;
              if (priorCds) return priorCds;
              return {
                ...entry,
                state: 'error',
                error: failures[entry.name],
              };
            }
            // Defensive: a file in this batch that produced neither a success nor a failure must not be
            // left spinning. Unreachable in the current flow, but guards against uploadFiles ever
            // returning an unclassified result.
            if (entry.state === 'uploading') {
              return {
                ...entry,
                state: 'error' as const,
                error: intl.formatMessage({ id: 'rag.file-upload.general-error' }, { file: entry.name }),
              };
            }
            return entry;
          }),
        }));

        // Ask the server about this bot's documents again now that one has been queued.
        //
        // Polling decides whether to keep going from the last response alone, so it cannot see a
        // document that response predates. On a bot whose existing documents had all finished, the
        // interval was already off and nothing would have turned it back on: the new file would spin
        // as 'processing' forever and hold the wizard shut. The upload completing is an independent
        // fact — not the state the poll exists to correct — so triggering a read on it is safe.
        if (useCds) void client.invalidateQueries({ queryKey: toolDocumentsQueryKey(tempToolCustomization.id) });

        // CDS same-name replace: delete the stale document(s) whose id differs from the just-uploaded one,
        // so re-uploading a file with the same name doesn't leave the old content searchable.
        if (useCds) {
          const stale = incomingFiles
            .map((f) => ({ name: f.name, prior: priorCdsEntryByName.get(f.name)?.fileId, next: idByName.get(f.name) }))
            .filter((x): x is { name: string; prior: string; next: string } => Boolean(x.prior && x.next && x.prior !== x.next));
          if (stale.length > 0) {
            // Delete all superseded docs in one call. removeFilesFromCds never throws, and 404s count as
            // success; a genuine non-404 delete failure surfaces ONLY via failedDeletions. Surface it
            // (log + analytics) so a stale doc left active in the bot's retrieval is detectable instead of
            // silent. Not toasted — the upload itself succeeded and this is best-effort cleanup.
            const res = await removeFilesFromCds(
              stale.map((s) => ({ name: s.name, fileId: s.prior })),
              tempToolCustomization.id,
            );
            const failed = res.data?.failedDeletions ?? [];
            if (res.errorMsg || failed.length > 0) {
              analyticsTrack('GenerationForm:FileRemoveError', { type: 'StaleReplaceCleanupFailed' });
              logger.error('CDS same-name replace: failed to delete stale document(s)', {
                errorMsg: res.errorMsg,
                failedDeletions: failed,
              });
            }
          }
        }
      } catch (error) {
        // uploadFiles can throw before any upload starts (missing Supabase session, failed RAG-assistant
        // fetch). Without this the batch would stay 'uploading' forever. Flip this batch's still-uploading
        // files to 'error' so they surface and become removable.
        logger.error('Failed to upload files', error);
        analyticsTrack('GenerationForm:FileUploadError', { type: 'UploadError' });
        set((state) => ({
          files: state.files.map((entry) => {
            if (!(entry.state === 'uploading' && incomingFiles.some((f) => f.name === entry.name))) return entry;
            // CDS: nothing was uploaded, so a same-name replace's prior doc is still live — restore the
            // prior entry so it stays removable rather than stranding it behind a fileId-less error row.
            const priorCds = useCds ? priorCdsEntryByName.get(entry.name) : undefined;
            if (priorCds) return priorCds;
            return {
              ...entry,
              state: 'error' as const,
              error: intl.formatMessage({ id: 'rag.file-upload.general-error' }, { file: entry.name }),
            };
          }),
        }));
        toast({
          title: intl.formatMessage({ id: 'rag.file-upload.general-error' }, { file: incomingFiles.map((f) => f.name).join(', ') }),
          status: 'error',
        });
      }
    },
    failQueuedUploads: (pending, intl) => {
      // Shared by the poll `.catch` and the reconciliation branch. Flips any file still 'uploading' and
      // present in `pending` to 'error' + a single toast. Guarded on actually-affected files so a timeout
      // with nothing queued shows no toast, and a repeat call can't double-toast (nothing stays 'uploading'
      // after the first flip).
      const affected = get().files.filter((entry) => entry.state === 'uploading' && pending.some((f) => f.name === entry.name));
      if (affected.length === 0) return;

      set((state) => ({
        files: state.files.map((entry) =>
          affected.some((a) => a.name === entry.name)
            ? { ...entry, state: 'error' as const, error: intl.formatMessage({ id: 'rag.file-upload.timeout-error' }) }
            : entry,
        ),
      }));
      toast({ title: intl.formatMessage({ id: 'rag.file-upload.timeout-error' }), status: 'error' });
    },
    onFilesRemoved: async (filesToRemove, intl) => {
      if (!filesToRemove.length) return;
      const { tempToolCustomization, files: currentFiles, cdsWritePathEnabled } = get();
      if (!tempToolCustomization) return;

      // EAS-365 (4b): migrated + admitted bots delete from CDS by document id; others use the legacy route.
      const useCds = cdsWritePathEnabled && isChatbotMigrationAdmitted(tempToolCustomization);

      // What makes a file deletable server-side differs by path, and the two must not be conflated.
      //
      // CDS deletes by document id, so the id is the test — and a document has one while it is still
      // indexing and after indexing failed, not only when it is ready to search. Keying this on
      // state === 'idle' meant a failed document was dropped from the list while its row stayed live:
      // it came back on reload and the bot kept reporting documents the teacher believed gone.
      //
      // Legacy deletes by filename and has no ingestion of its own, so a listed file is a stored file.
      // Its rows are rendered server-side by getAssistantToolFiles, which returns no id at all — so
      // asking for one here would classify every one of them as local-only and silently delete nothing.
      const isServerBacked = (name: string) => {
        const entry = currentFiles.find((f) => f.name === name);
        if (!entry) return false;
        return useCds ? Boolean(entry.fileId) : entry.state === 'idle';
      };
      const serverBacked = filesToRemove.filter((f) => isServerBacked(f.name));
      const localOnly = filesToRemove.filter((f) => !isServerBacked(f.name));

      if (localOnly.length > 0) {
        set((state) => ({
          files: state.files.filter((entry) => !localOnly.some((f) => f.name === entry.name)),
        }));
      }

      if (serverBacked.length === 0) return;

      // Remember what each file was before the optimistic 'removing' overwrites it, so a failed
      // deletion can put it back as it was. Restoring everything to 'idle' would report a document
      // that is still indexing, or whose indexing failed, as ready to search — the deletion failing
      // says nothing about the document's ingestion.
      const priorStateByName = new Map(currentFiles.map((entry) => [entry.name, entry.state]));

      // mark the server-backed files as removing
      set((state) => ({
        files: state.files.map((entry) =>
          serverBacked.some((f) => f.name === entry.name) ? { ...entry, state: 'removing' as const } : entry,
        ),
      }));

      // Keep the files listed when deletion genuinely fails, so we never pretend a file is gone
      // while the server still has it — restored to whatever they were, not to ready.
      const restorePriorState = () =>
        set((state) => ({
          files: state.files.map((entry) =>
            serverBacked.some((f) => f.name === entry.name)
              ? { ...entry, state: priorStateByName.get(entry.name) ?? ('idle' as const) }
              : entry,
          ),
        }));

      const response = useCds
        ? await removeFilesFromCds(serverBacked, tempToolCustomization.id)
        : await removeFiles(serverBacked, tempToolCustomization.id);
      if (response.errorMsg) {
        analyticsTrack('GenerationForm:FileRemoveError', { type: 'RemoveError', errorMsg: response.errorMsg });
        restorePriorState();
        toast({ title: intl.formatMessage({ id: 'rag.file-remove.error' }), status: 'error' });
        return;
      }

      if (!response.data) {
        analyticsTrack('GenerationForm:FileRemoveError', { type: 'Unexpected' });
        logger.error('No data in response from removeFiles');
        restorePriorState();
        toast({ title: intl.formatMessage({ id: 'rag.file-remove.error' }), status: 'error' });
        return;
      }

      analyticsTrack('GenerationForm:FileRemove');
      const { successfulDeletions, failedDeletions } = response.data;
      set((state) => ({
        files: state.files
          .map((entry) => {
            if (!serverBacked.some((f) => f.name === entry.name)) return entry;
            if (successfulDeletions.includes(entry.name)) return null;
            // Still in storage — keep it listed, in the state it was in before the delete attempt.
            // Its ingestion is unaffected by the deletion failing. Surface the per-file reason too.
            const fail = failedDeletions?.find((f) => f.file === entry.name);
            return { ...entry, state: priorStateByName.get(entry.name) ?? ('idle' as const), ...(fail ? { error: fail.error } : {}) };
          })
          .filter((entry): entry is DownloadableFile<FileState> => Boolean(entry)),
      }));

      if (failedDeletions?.length) {
        analyticsTrack('GenerationForm:FileRemoveError', { type: 'PartialFailure', failedCount: failedDeletions.length });
        logger.error('Some files failed to delete:', failedDeletions);
        toast({ title: intl.formatMessage({ id: 'rag.file-remove.error' }), status: 'error' });
      }
    },
    setStep: async (step) => {
      const { isAssistantTool, tempToolCustomization } = get();

      if (!tempToolCustomization) return;

      if (isAssistantTool()) {
        if (step === 'prepare') {
          updateToolCustomizationServerAction(tempToolCustomization.id, {
            json_config: tempToolCustomization.json_config as Json,
          })
            .then(({ error }) => {
              if (error) {
                logger.error('Error updating tool customization', error);
              }
            })
            .catch((e) => {
              logger.error('Unknown error updating tool customization', e);
            });
        }
      }

      set({ customizingStep: step });
    },
    isAssistantTool(): boolean {
      return !!get().toolToCustomize?.tool.assistant_tool_type;
    },
    clearCustomizationTool(): void {
      set({ toolToCustomize: null });
    },
  }),
);
